import { DropdownFilterItemProps } from "../../controls/filterBar/dropdownFilterItem";

/**
 * Filtering for the Container Management lists.
 *
 * Both Kubernetes endpoints return the whole collection in one response, so
 * every filter here runs client-side over data already in memory. That keeps
 * filtering instant and avoids round-trips; `fieldSelector` could not express
 * most of these anyway.
 */

/** Sentinel for "no filter applied". */
export const ANY = "";

/**
 * Labels Kubernetes sets on every node. They duplicate columns the table
 * already has - arch is the Architecture column, hostname is the Name column,
 * and os is "linux" on every node in the fleet - so they are hidden and only
 * labels someone deliberately applied are shown.
 */
const STANDARD_LABEL_PREFIXES = ["kubernetes.io/", "beta.kubernetes.io/"];

const isStandardLabel = (key: string): boolean => STANDARD_LABEL_PREFIXES.some((prefix) => key.indexOf(prefix) === 0);

/** "hardware=rpi" for each label that is not one Kubernetes sets itself. */
export const customLabels = (labels?: { [key: string]: string }): string[] =>
    Object.keys(labels || {})
        .filter((key) => !isStandardLabel(key))
        .sort()
        .map((key) => `${key}=${(labels || {})[key]}`);

/** Distinct, sorted, blank-free values of one field across the rows. */
export const distinctValues = <T>(rows: T[], pick: (row: T) => string | string[]): string[] => {
    const seen: { [value: string]: true } = {};

    rows.forEach((row) => {
        const value = pick(row);
        (Array.isArray(value) ? value : [value]).forEach((v) => {
            if (v) {
                seen[v] = true;
            }
        });
    });

    return Object.keys(seen).sort();
};

/** Build dropdown items with a leading "any" entry that is selected by default. */
export const filterItems = (values: string[], anyText: string): DropdownFilterItemProps[] => [
    { text: anyText, value: ANY, default: true },
    ...values.map((value) => ({ text: value, value })),
];

/** A pod's ready column is "1/2"; it is only fully ready when both sides match. */
export const isFullyReady = (ready: string): boolean => {
    const parts = /^(\d+)\/(\d+)$/.exec(ready || "");

    if (!parts) {
        return false;
    }

    const have = parseInt(parts[1], 10);
    const want = parseInt(parts[2], 10);

    return want > 0 && have === want;
};

/** Restart buckets. The fleet has containers in the thousands, so "many" earns its place. */
export const RESTART_ANY = ANY;
export const RESTART_SOME = "some";
export const RESTART_MANY = "many";
const MANY_RESTARTS = 100;

export const matchesRestarts = (restarts: number, bucket: string): boolean => {
    if (bucket === RESTART_SOME) {
        return restarts > 0;
    }
    if (bucket === RESTART_MANY) {
        return restarts >= MANY_RESTARTS;
    }

    return true;
};

/** A filter passes when nothing is selected, or the value matches exactly. */
export const matches = (value: string, selected: string): boolean => !selected || value === selected;

/** Phase is rendered as "Failed (Evicted)"; filter on the phase itself. */
export const matchesPhase = (phase: string, selected: string): boolean =>
    !selected || phase === selected || phase.indexOf(`${selected} (`) === 0;

/** Filter passes when the row carries the selected label, or none is selected. */
export const matchesLabel = (labels: string[], selected: string): boolean => !selected || labels.indexOf(selected) >= 0;
